import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { useNavigate } from '@tanstack/react-router'
import NAV_ITEMS, { BOTTOM_NAV_ITEMS } from '@/lib/constants'
import { useAuth } from '@/lib/hooks/useAuth'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { SidebarIcon } from 'lucide-react'

interface SidebarProps {
  minimized?: boolean
  onToggleMinimize?: () => void
}

export function Sidebar({ minimized = false, onToggleMinimize }: SidebarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { activeCrisis } = useCrisisStore()

  // Use current pathname to determine active item. TanStack router navigate doesn't expose
  // a hook for pathname in this project pattern, so fallback to window.location.pathname
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'

  // Helper to check if user has required permissions for a nav item
  const hasNavItemPermission = useMemo(() => {
    return (item: (typeof NAV_ITEMS)[0]): boolean => {
      // If no permissions required, allow access
      if (!item.requiredPermissions || item.requiredPermissions.length === 0) return true

      // No user = no permissions
      if (!user) return false

      // Super admin bypass
      if (user.isSuperAdmin) return true

      // Check if user has any of the required permissions (OR logic)
      const userPermissions = user.permissions || []
      return item.requiredPermissions.some(perm => userPermissions.includes(perm))
    }
  }, [user])

  // Filter nav items based on permissions and crisis state
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter(item => {
      // Permission check (replaces adminOnly)
      if (!hasNavItemPermission(item)) return false

      // Items that require crisis only show when there's an active crisis
      if (item.requiresCrisis && !activeCrisis) return false

      // Items that don't require crisis only show when there's no active crisis
      if (item.requiresCrisis === false && activeCrisis) return false

      return true
    })
  }, [hasNavItemPermission, activeCrisis])

  // Filter bottom nav items based on permissions and authentication
  const visibleBottomNavItems = useMemo(() => {
    return BOTTOM_NAV_ITEMS.filter(item => {
      // Show login only when user is NOT authenticated
      if (item.id === 'login' && user) {
        return false
      }

      // Only show Profile and Logout when user is authenticated
      if ((item.id === 'profile' || item.id === 'logout') && !user) {
        return false
      }

      // Permission check
      return hasNavItemPermission(item)
    })
  }, [hasNavItemPermission, user])

  // Handle navigation for bottom items (special case for logout)
  const handleBottomNavClick = async (item: (typeof BOTTOM_NAV_ITEMS)[0]) => {
    if (item.id === 'logout') {
      await logout()
      // Redirect to login after logout completes
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    } else {
      navigate({ to: item.to })
    }
  }

  return (
    <aside className={`sidebar ${minimized ? 'minimized' : ''} transition-all`}>
      <div className={`brand flex items-center justify-between space-x-1 h-[52px] py-1 ${minimized ? 'group' : ''}`}>
        <img
          src="/crisisbuddy_logo.png"
          alt="BGCH Crisis Buddy logo"
          className={`object-contain ${minimized ? 'w-8 group-hover:hidden' : 'h-[100%]'}`}
        />
        {/* {!minimized && <strong className="text-white text-xs">Crisiscommunicatie Buddy</strong>} */}
        <button
          onClick={onToggleMinimize}
          className={minimized ? 'hidden group-hover:flex z-10 p-1 transition-all rounded-md hover:bg-white/20 text-white hover:text-orange-400 cursor-pointer items-center justify-center' : 'z-10 p-1 transition-all rounded-md hover:bg-white/20 text-white hover:text-orange-400 cursor-pointer'}
          aria-label={minimized ? 'Open menu' : 'Sluit menu'}
          title={minimized ? 'Open menu' : 'Sluit menu'}
        >
          <SidebarIcon className="w-6 h-6" />
        </button>
      </div>

      <nav className="mt-0 flex-1" aria-label="Hoofdnavigatie">
        <ul className="space-y-1" role="list">
          {visibleNavItems.map(item => {
            const isActive = pathname === item.to
            return (
              <li key={item.id}>
                <Button
                  variant={item.variant ?? 'white'}
                  className={['nav-item', isActive ? 'active' : ''].join(' ')}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => navigate({ to: item.to })}
                >
                  <span aria-hidden="true">{item.icon({ className: 'w-5 h-5' })}</span>
                  {!minimized && <span className="ml-2">{t(item.titleKey)}</span>}
                </Button>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="mt-auto">
        {visibleBottomNavItems.length > 0 && (
          <nav aria-label="Gebruikersnavigatie">
            <ul className="space-y-1" role="list">
              {visibleBottomNavItems.map(item => {
                const isActive = pathname === item.to
                return (
                  <li key={item.id}>
                    <Button
                      variant={item.variant ?? 'white'}
                      className={['nav-item', isActive ? 'active' : ''].join(' ')}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleBottomNavClick(item)}
                    >
                      <span aria-hidden="true">{item.icon({ className: 'w-5 h-5' })}</span>
                      {!minimized && <span className="ml-2">{t(item.titleKey)}</span>}
                    </Button>
                  </li>
                )
              })}
            </ul>
          </nav>
        )}
      </div>
    </aside>
  )
}

export default Sidebar
